import {
    ACCOUNTING_DIVISION,
    buildAccountingQueryParams,
    getAccountingDivisionFromMetadata,
} from "@/utils/accountingBookingUtils";

export { buildAccountingQueryParams, ACCOUNTING_DIVISION };

export const DEFAULT_CURRENCY_CODE = "USD";

/**
 * Resolve company_id + division for any accounting API call.
 */
export function resolveAccountingContext({
    companyId,
    accountingDivision,
    metadata,
    existingDoc,
}) {
    const division =
        getAccountingDivisionFromMetadata(metadata?.division) ||
        existingDoc?.division ||
        accountingDivision ||
        ACCOUNTING_DIVISION.EXPORT;

    const parsedCompanyId = Number(companyId);
    return {
        company_id:
            Number.isFinite(parsedCompanyId) && parsedCompanyId > 0
                ? parsedCompanyId
                : 1,
        division,
    };
}

export function buildAccountingListQuery({
    companyId,
    division,
    filters = {},
}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
        }
    });
    if (companyId != null && companyId !== "") {
        params.set("company_id", String(companyId));
    }
    if (division) {
        params.set("division", division);
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

export function calculateDocumentTotals(lines = []) {
    const subtotal = lines.reduce(
        (sum, line) => sum + (Number(line?.amount) || 0),
        0,
    );
    const taxAmount = lines.reduce(
        (sum, line) => sum + (Number(line?.tax_amount) || 0),
        0,
    );
    return {
        subtotal,
        taxAmount,
        totalAmount: subtotal + taxAmount,
    };
}

/**
 * Normalize line items to match backend InvoiceLineCreate / BillLineCreate.
 */
export function normalizeAccountingLines(lines = [], bookingId = "") {
    const headerBookingId =
        bookingId !== undefined && bookingId !== null && bookingId !== ""
            ? String(bookingId)
            : "";

    return (Array.isArray(lines) ? lines : []).map((line, index) => {
        const quantity = Number(line?.quantity) || 1;
        const unitPrice =
            Number(line?.unit_price ?? line?.amount) || 0;
        const amount =
            Number(line?.amount) || quantity * unitPrice;
        const taxRate = Number(line?.tax_rate) || 0;
        const taxAmount = Number(line?.tax_amount) || 0;
        const totalAmount =
            Number(line?.total_amount) || amount + taxAmount;
        const chargeCodeId = String(
            line?.charge_code_id ||
                line?.chargeCode ||
                line?.CHARGE_CODE ||
                "",
        );

        const normalized = {
            line_no: line?.line_no ?? index + 1,
            description: String(line?.description || ""),
            chargeCode: chargeCodeId,
            charge_code_id: chargeCodeId,
            gl_account_id: line?.gl_account_id || "",
            roe: line?.roe != null ? Number(line.roe) : null,
            Basis: line?.Basis || line?.basis || "Per Shipment",
            quantity,
            unit_price: unitPrice,
            amount,
            tax_rate: taxRate,
            tax_amount: taxAmount,
            total_amount: totalAmount,
            currency: line?.currency || DEFAULT_CURRENCY_CODE,
            exchange_rate:
                line?.exchange_rate != null
                    ? Number(line.exchange_rate)
                    : line?.roe != null
                      ? Number(line.roe)
                      : 1,
            source_charge_id: line?.source_charge_id
                ? String(line.source_charge_id)
                : "",
            booking_id: line?.booking_id
                ? String(line.booking_id)
                : headerBookingId,
            comments: line?.comments ?? null,
        };

        if (line?.id) {
            normalized.id = line.id;
        }

        return normalized;
    });
}

export function buildCustomerInvoicePayload(formData, metadata, context) {
    const ctx = resolveAccountingContext(context);
    const bookingId = String(
        formData?.booking_id || metadata?.booking_id || "",
    ).trim();
    const lines = normalizeAccountingLines(formData?.lines, bookingId);
    const { subtotal, taxAmount, totalAmount } =
        calculateDocumentTotals(lines);
    const currencyCode =
        formData?.currency_code || DEFAULT_CURRENCY_CODE;

    return {
        invoice_date: formData?.invoice_date || "",
        due_date: formData?.due_date || "",
        customer_id: String(formData?.customer_id || ""),
        customer_name: formData?.customer_name || "",
        vendor_invoice_no:
            formData?.customer_invoice_no ||
            formData?.vendor_invoice_no ||
            "",
        booking_id: bookingId,
        origin_port_id:
            formData?.origin_port_id || metadata?.origin_code || "",
        destination_port_id:
            formData?.destination_port_id ||
            metadata?.destination_code ||
            "",
        currency_id: formData?.currency_id || currencyCode,
        currency_code: currencyCode,
        exchange_rate: Number(formData?.exchange_rate) || 1,
        invoice_type: formData?.invoice_type || "INVOICE",
        subtotal,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        terms: formData?.terms || "",
        notes: formData?.notes || "",
        internal_notes: formData?.internal_notes || "",
        metadata: metadata || {},
        lines,
        company_id: ctx.company_id,
        division: ctx.division,
    };
}

export function buildVendorBillPayload(formData, metadata, context) {
    const ctx = resolveAccountingContext(context);
    const bookingId = String(
        formData?.booking_id || metadata?.booking_id || "",
    ).trim();
    const lines = normalizeAccountingLines(formData?.lines, bookingId);
    const { subtotal, taxAmount, totalAmount } =
        calculateDocumentTotals(lines);
    const currencyCode =
        formData?.currency_code || DEFAULT_CURRENCY_CODE;

    return {
        bill_date: formData?.bill_date || "",
        due_date: formData?.due_date || "",
        vendor_id: String(formData?.vendor_id || ""),
        vendor_name: formData?.vendor_name || "",
        vendor_invoice_no: formData?.vendor_invoice_no || "",
        booking_id: bookingId,
        origin_port_id:
            formData?.origin_port_id || metadata?.origin_code || "",
        destination_port_id:
            formData?.destination_port_id ||
            metadata?.destination_code ||
            "",
        currency_id: formData?.currency_id || currencyCode,
        currency_code: currencyCode,
        exchange_rate: Number(formData?.exchange_rate) || 1,
        invoice_type: formData?.invoice_type || "BILL",
        subtotal,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        terms: formData?.terms || "",
        notes: formData?.notes || "",
        internal_notes: formData?.internal_notes || "",
        metadata: metadata || {},
        lines,
        company_id: ctx.company_id,
        division: ctx.division,
    };
}

export async function parseAccountingApiError(response) {
    try {
        const body = await response.json();
        const detail = body?.detail;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
            return detail
                .map((d) => d?.msg || d?.message || JSON.stringify(d))
                .join("; ");
        }
        return body?.message || `Request failed (${response.status})`;
    } catch {
        return `Request failed (${response.status})`;
    }
}

export async function accountingFetch(url, options = {}) {
    const response = await fetch(url, {
        headers: { "Content-Type": "application/json", ...options.headers },
        ...options,
    });
    if (!response.ok) {
        throw new Error(await parseAccountingApiError(response));
    }
    if (response.status === 204) return null;
    return response.json();
}
